import React, { useState, useEffect } from 'react';
import {
  Create,
  Visibility,
  Edit,
  Delete,
  FilterList,
  Search,
  Add,
  Refresh,
  Schedule,
  AttachMoney,
  Person,
  Assignment,
  PendingActions,
  WorkOutline,
  Done,
  Cancel,
  Warning,
  CheckCircle,
  Error,
  Info,
  Download,
  Upload,
  Star,
  StarBorder,
  Check,
  Close,
  ArrowForward,
  CalendarToday,
  AccessTime,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { designCaseApi } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

const DesignCaseList = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTab, setFilterTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [caseToCancel, setCaseToCancel] = useState(null);

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      const response = await designCaseApi.getSellerCases(token);
      setCases(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast.error('Không thể tải danh sách design cases');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'waiting_approval':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <PendingActions className="w-4 h-4" />;
      case 'in_progress':
        return <WorkOutline className="w-4 h-4" />;
      case 'waiting_approval':
        return <Schedule className="w-4 h-4" />;
      case 'approved':
        return <Done className="w-4 h-4" />;
      case 'rejected':
        return <Cancel className="w-4 h-4" />;
      case 'cancelled':
        return <Cancel className="w-4 h-4" />;
      default:
        return <Assignment className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Chờ xử lý';
      case 'in_progress':
        return 'Đang thực hiện';
      case 'waiting_approval':
        return 'Chờ duyệt';
      case 'approved':
        return 'Đã duyệt';
      case 'rejected':
        return 'Từ chối';
      case 'cancelled':
        return 'Đã hủy';
      default:
        return 'Không xác định';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN');
  };

  const handleViewCase = (case_) => {
    setSelectedCase(case_);
    setShowDetailDialog(true);
  };

  const handleReviewCase = (case_) => {
    setSelectedCase(case_);
    setShowReviewDialog(true);
  };

  const handleApproveCase = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      await designCaseApi.approveDesignCase(token, selectedCase.id);
      
      toast.success('Đã duyệt design case!');
      setShowReviewDialog(false);
      fetchCases();
    } catch (error) {
      console.error('Error approving case:', error);
      toast.error('Có lỗi xảy ra khi duyệt case');
    }
  };

  const handleRejectCase = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      await designCaseApi.rejectDesignCase(token, selectedCase.id,);
      
      toast.success('Đã từ chối design case!');
      setShowReviewDialog(false);
      fetchCases();
    } catch (error) {
      console.error('Error rejecting case:', error);
      toast.error('Có lỗi xảy ra khi từ chối case');
    }
  };

  const handleApproveDesign = async (caseId, file) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      await designCaseApi.approveDesign(token, caseId, file);
      toast.success('Đã chấp nhận design file!');
      fetchCases();
    } catch (error) {
      console.error('Error approving design file:', error);
      toast.error('Có lỗi xảy ra khi chấp nhận design file');
    }
  };

  const handleRejectDesign = async (caseId, file) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      await designCaseApi.rejectDesign(token, caseId, file);
      toast.success('Đã từ chối design file!');
      fetchCases();
    } catch (error) {
      console.error('Error rejecting design file:', error);
      toast.error('Có lỗi xảy ra khi từ chối design file');
    }
  };

  const handleCancelCase = async (caseId) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      await designCaseApi.cancelDesignCase(token, caseId);
      toast.success('Đã hủy design case!');
      setShowCancelDialog(false);
      setCaseToCancel(null);
      fetchCases();
    } catch (error) {
      console.error('Error cancelling case:', error);
      toast.error('Có lỗi xảy ra khi hủy case');
    }
  };

  const openCancelDialog = (case_) => {
    setCaseToCancel(case_);
    setShowCancelDialog(true);
  };

  const filteredCases = cases.filter(case_ => {
    const matchesSearch = (case_.product_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (case_.order_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (case_.id || '').toString().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || case_.status === statusFilter;
    const matchesTab = filterTab === 0 || 
                      (filterTab === 1 && case_.status === 'pending') ||
                      (filterTab === 2 && case_.status === 'in_progress') ||
                      (filterTab === 3 && case_.status === 'waiting_approval') ||
                      (filterTab === 4 && case_.status === 'approved');
    return matchesSearch && matchesStatus && matchesTab;
  });

  const tabLabels = ['Tất cả', 'Chờ xử lý', 'Đang thực hiện', 'Chờ duyệt', 'Đã duyệt'];

  if (loading) {
    return (
      <div className="w-full mt-8">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        {/* Welcome Message */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 rounded-full p-2">
                <Create className="text-white text-2xl" />
              </div>
              <div>
                <h1 className="text-white text-2xl sm:text-3xl font-bold">
                  Quản Lý Design Cases
                </h1>
                <p className="text-blue-100 text-sm sm:text-base">
                  Theo dõi và quản lý các design cases của bạn
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Tổng Cases</p>
                <p className="text-3xl font-bold">{cases.length}</p>
              </div>
              <Assignment className="text-4xl text-blue-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium">Chờ xử lý</p>
                <p className="text-3xl font-bold">{cases.filter(c => c.status === 'pending').length}</p>
              </div>
              <PendingActions className="text-4xl text-yellow-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Đang thực hiện</p>
                <p className="text-3xl font-bold">{cases.filter(c => c.status === 'in_progress').length}</p>
              </div>
              <WorkOutline className="text-4xl text-purple-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Đã hoàn thành</p>
                <p className="text-3xl font-bold">{cases.filter(c => c.status === 'approved').length}</p>
              </div>
              <CheckCircle className="text-4xl text-green-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-pink-100 text-sm font-medium">Tổng chi phí</p>
                <p className="text-lg font-bold">{formatCurrency(cases.reduce((total, c) => total + (c.cost_per_design || 0), 0))}</p>
              </div>
              <AttachMoney className="text-4xl text-pink-200" />
            </div>
          </motion.div>
        </div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-8"
        >
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {tabLabels.map((label, index) => (
              <button
                key={label}
                onClick={() => setFilterTab(index)}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                  filterTab === index
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Tìm kiếm theo tên sản phẩm hoặc mã đơn hàng..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="pending">Chờ xử lý</option>
              <option value="in_progress">Đang thực hiện</option>
              <option value="waiting_approval">Chờ duyệt</option>
              <option value="approved">Đã duyệt</option>
              <option value="rejected">Từ chối</option>
              <option value="cancelled">Đã hủy</option>
            </select>
            
            <button
              onClick={fetchCases}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300"
            >
              <Refresh className="w-5 h-5" />
              <span>Làm mới</span>
            </button>
          </div>
        </motion.div>

        {/* Cases List */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-bold text-gray-800">
              Danh Sách Cases ({filteredCases.length})
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Case</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Designer</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Trạng thái</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Ưu tiên</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Deadline</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Chi phí</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {filteredCases.map((case_, index) => (
                    <motion.tr
                      key={case_.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="hover:bg-gray-50 transition-all duration-300"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <Create className="text-white text-sm" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {case_.order_id || `Case #${case_.id}`}
                            </p>
                            <p className="text-sm text-gray-500">
                              {case_.product_name || 'Sản phẩm không xác định'}
                            </p>
                            <p className="text-xs text-gray-400">
                              Tạo: {formatDate(case_.created_at)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <Person className="text-gray-500 text-sm" />
                          </div>
                          <span className="text-sm text-gray-700">
                            {case_.designer_name || 'Chưa assign'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(case_.status)}`}>
                          {getStatusIcon(case_.status)}
                          <span className="ml-1">{getStatusText(case_.status)}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(case_.priority)}`}>
                          {case_.priority || 'medium'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm text-gray-700">
                            {formatDateTime(case_.deadline)}
                          </p>
                          {new Date(case_.deadline) < new Date() && case_.status !== 'approved' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 mt-1">
                              Quá hạn
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-gray-900">
                          {formatCurrency(case_.cost_per_design || 0)}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewCase(case_)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
                            title="Xem chi tiết"
                          >
                            <Visibility className="w-4 h-4" />
                          </button>
                          {case_.status === 'waiting_approval' && (
                            <>
                              <button
                                onClick={() => handleApproveCase(case_)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all duration-300"
                                title="Chấp nhận"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRejectCase(case_)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300"
                                title="Từ chối"
                              >
                                <Close className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {case_.status === 'pending' && (
                            <button
                              onClick={() => openCancelDialog(case_)}
                              className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-all duration-300"
                              title="Hủy case"
                            >
                              <Cancel className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {filteredCases.length === 0 && (
            <div className="text-center py-12">
              <Create className="text-gray-300 text-6xl mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-500 mb-2">
                Không tìm thấy cases nào
              </h3>
              <p className="text-gray-400">
                Thử thay đổi bộ lọc hoặc tạo case mới
              </p>
            </div>
          )}
        </motion.div>

        {/* Floating Action Button */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="fixed bottom-6 right-6 z-50"
        >
          <button className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center">
            <Add className="text-2xl" />
          </button>
        </motion.div>
      </motion.div>

      {/* Case Detail Dialog */}
      {showDetailDialog && selectedCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header with Status-based Styling */}
            <div className={`p-6 text-white relative ${
              selectedCase.status === 'pending' 
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500' 
                : selectedCase.status === 'in_progress'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500'
                : selectedCase.status === 'waiting_approval'
                ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                : selectedCase.status === 'approved'
                ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                : selectedCase.status === 'rejected'
                ? 'bg-gradient-to-r from-red-500 to-pink-500'
                : 'bg-gradient-to-r from-gray-500 to-gray-600'
            }`}>
              {/* Close Button */}
              <motion.button
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowDetailDialog(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white backdrop-blur-sm transition-all duration-300 z-10"
              >
                <Close className="w-5 h-5" />
              </motion.button>
              
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-2xl font-bold mb-2"
                  >
                    Case #{selectedCase.id}
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-white/90 text-sm"
                  >
                    Mã sản phẩm: {selectedCase.product_id || 'không xác định'}
                  </motion.p>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-white/80 text-sm mt-1"
                  >
                    Khách hàng: {selectedCase.customer_name || 'N/A'}
                  </motion.p>
                </div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-right"
                >
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/20 border border-white/30 backdrop-blur-sm`}>
                    {getStatusIcon(selectedCase.status)}
                    <span className="ml-2">{getStatusText(selectedCase.status)}</span>
                  </span>
                  <div className="mt-2 text-white/80 text-xs">
                    {selectedCase.status === 'pending' && 'Chờ designer nhận case'}
                    {selectedCase.status === 'in_progress' && 'Designer đang làm việc'}
                    {selectedCase.status === 'waiting_approval' && 'Chờ seller phản hồi'}
                    {selectedCase.status === 'approved' && 'Đã hoàn thành'}
                    {selectedCase.status === 'rejected' && 'Đã từ chối'}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Info */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    📋 Thông tin cơ bản
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Thời gian tạo:</span>
                      <span className="font-semibold text-sm">{formatDate(selectedCase.created_at)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Deadline:</span>
                      <span className={`font-semibold text-sm ${
                        new Date(selectedCase.deadline) < new Date() ? 'text-red-600' : 'text-gray-800'
                      }`}>
                        {formatDate(selectedCase.deadline)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Độ ưu tiên:</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(selectedCase.priority)}`}>
                        {selectedCase.priority || 'medium'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Chi phí:</span>
                      <span className="font-bold text-green-600 text-lg">
                        {formatCurrency(selectedCase.cost_per_design || 0)}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Designer Info */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 border border-green-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    🎨 Designer
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-semibold text-sm">{selectedCase.designer_email || 'Chưa assign'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Seller:</span>
                      <span className="font-semibold text-sm">{selectedCase.seller_email || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Khách hàng:</span>
                      <span className="font-semibold text-sm">{selectedCase.customer_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Email KH:</span>
                      <span className="font-semibold text-sm">{selectedCase.customer_email || 'N/A'}</span>
                    </div>
                  </div>
                </motion.div>

                {/* Timeline */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-blue-50 to-cyan-100 rounded-2xl p-6 border border-blue-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    ⏰ Timeline
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Tạo:</span>
                      <span className="font-semibold text-sm">{formatDate(selectedCase.created_at)}</span>
                    </div>
                    {selectedCase.received_at && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Nhận:</span>
                        <span className="font-semibold text-sm">{formatDate(selectedCase.received_at)}</span>
                      </div>
                    )}
                    {selectedCase.completed_at && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Hoàn thành:</span>
                        <span className="font-semibold text-sm">{formatDate(selectedCase.completed_at)}</span>
                      </div>
                    )}
                    {selectedCase.approved_at && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Duyệt:</span>
                        <span className="font-semibold text-sm">{formatDate(selectedCase.approved_at)}</span>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Status Progress */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-2xl p-6 border border-purple-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    📊 Tiến độ
                  </h3>
                  <div className="space-y-4">
                    {/* Progress Steps */}
                    <div className="relative">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${selectedCase.status !== 'pending' ? 'text-green-600' : 'text-gray-400'}`}>
                          Designer nhận case
                        </span>
                        {selectedCase.status !== 'pending' && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${['in_progress', 'waiting_approval', 'approved', 'rejected'].includes(selectedCase.status) ? 'text-green-600' : 'text-gray-400'}`}>
                          Designer làm việc
                        </span>
                        {['in_progress', 'waiting_approval', 'approved', 'rejected'].includes(selectedCase.status) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${['waiting_approval', 'approved', 'rejected'].includes(selectedCase.status) ? 'text-green-600' : 'text-gray-400'}`}>
                          Chờ phản hồi
                        </span>
                        {['waiting_approval', 'approved', 'rejected'].includes(selectedCase.status) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${['approved', 'rejected'].includes(selectedCase.status) ? 'text-green-600' : 'text-gray-400'}`}>
                          Hoàn thành
                        </span>
                        {['approved', 'rejected'].includes(selectedCase.status) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Notes */}
              {selectedCase.note && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-6 bg-gradient-to-br from-yellow-50 to-amber-100 rounded-2xl p-6 border border-yellow-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    📝 Ghi chú
                  </h3>
                  <div className="bg-white/80 rounded-xl p-4 border border-yellow-200">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedCase.note}</p>
                  </div>
                </motion.div>
              )}

              {/* Design Images */}
              {selectedCase.design_files && selectedCase.design_files.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-6 bg-gradient-to-br from-emerald-50 to-green-100 rounded-2xl p-6 border border-green-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    🖼️ Design Images ({selectedCase.design_files.length})
                  </h3>
                  <div className={`${selectedCase.design_files.length > 4 ? 'flex overflow-x-auto gap-4 pb-4' : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'}`}>
                    {selectedCase.design_files.map((url, index) => (
                      <motion.div 
                        key={index} 
                        className={`group ${selectedCase.design_files.length > 4 ? 'flex-shrink-0 w-64' : ''}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.7 + index * 0.1 }}
                      >
                        <div className="relative">
                          <img
                            src={url.trim()}
                            alt={`Design ${index + 1}`}
                            className={`${selectedCase.design_files.length > 4 ? 'w-64 h-48' : 'w-full h-32'} object-cover rounded-xl shadow-lg group-hover:scale-105 transition-transform duration-300`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className={`hidden ${selectedCase.design_files.length > 4 ? 'w-64 h-48' : 'w-full h-32'} bg-gray-200 rounded-xl items-center justify-center`}>
                            <span className="text-gray-500 text-sm">Không thể tải hình</span>
                          </div>
                          {/* Image number badge */}
                          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                            {index + 1}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  {selectedCase.design_files.length > 4 && (
                    <div className="mt-2 text-center">
                      <span className="text-sm text-gray-600">← Kéo để xem thêm ảnh →</span>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Fallback for design_url if design_files is empty */}
              {(!selectedCase.design_files || selectedCase.design_files.length === 0) && selectedCase.design_url && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-6 bg-gradient-to-br from-emerald-50 to-green-100 rounded-2xl p-6 border border-green-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    🖼️ Design Images (Legacy)
                  </h3>
                  {(() => {
                    const imageUrls = selectedCase.design_url.split(',').filter(url => url.trim());
                    return (
                      <>
                        <div className={`${imageUrls.length > 4 ? 'flex overflow-x-auto gap-4 pb-4' : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'}`}>
                          {imageUrls.map((url, index) => (
                            <motion.div 
                              key={index} 
                              className={`group ${imageUrls.length > 4 ? 'flex-shrink-0 w-64' : ''}`}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.7 + index * 0.1 }}
                            >
                              <div className="relative">
                                <img
                                  src={url.trim()}
                                  alt={`Design ${index + 1}`}
                                  className={`${imageUrls.length > 4 ? 'w-64 h-48' : 'w-full h-32'} object-cover rounded-xl shadow-lg group-hover:scale-105 transition-transform duration-300`}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <div className={`hidden ${imageUrls.length > 4 ? 'w-64 h-48' : 'w-full h-32'} bg-gray-200 rounded-xl items-center justify-center`}>
                                  <span className="text-gray-500 text-sm">Không thể tải hình</span>
                                </div>
                                {/* Image number badge */}
                                <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                                  {index + 1}
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                        {imageUrls.length > 4 && (
                          <div className="mt-2 text-center">
                            <span className="text-sm text-gray-600">← Kéo để xem thêm ảnh →</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </motion.div>
              )}

              {/* Rejection Reason */}
              {selectedCase.rejection_reason && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="mt-6 bg-gradient-to-br from-red-50 to-pink-100 rounded-2xl p-6 border border-red-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    ❌ Lý do từ chối
                  </h3>
                  <div className="bg-white/80 rounded-xl p-4 border border-red-200">
                    <p className="text-red-700">{selectedCase.rejection_reason}</p>
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              {selectedCase.status === 'waiting_approval' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 text-white border border-purple-200"
                >
                  <h3 className="text-lg font-bold mb-4 text-center">
                    🎯 Quyết định
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleApproveCase()}
                      className="flex items-center justify-center space-x-2 px-8 py-3 bg-green-500 hover:bg-green-600 rounded-xl font-medium transition-all duration-300 shadow-lg"
                    >
                      <Check className="w-5 h-5" />
                      <span>Chấp nhận</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleRejectCase()}
                      className="flex items-center justify-center space-x-2 px-8 py-3 bg-red-500 hover:bg-red-600 rounded-xl font-medium transition-all duration-300 shadow-lg"
                    >
                      <Close className="w-5 h-5" />
                      <span>Từ chối</span>
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {/* Status-specific Messages */}
              {selectedCase.status === 'pending' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-6 text-white"
                >
                  <div className="text-center">
                    <PendingActions className="text-4xl mx-auto mb-3" />
                    <h3 className="text-lg font-bold mb-2">Chờ Designer Nhận Case</h3>
                    <p className="text-yellow-100">Case đã được tạo và đang chờ designer nhận để bắt đầu làm việc.</p>
                  </div>
                </motion.div>
              )}

              {selectedCase.status === 'in_progress' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-6 text-white"
                >
                  <div className="text-center">
                    <WorkOutline className="text-4xl mx-auto mb-3" />
                    <h3 className="text-lg font-bold mb-2">Designer Đang Làm Việc</h3>
                    <p className="text-blue-100">Designer đã nhận case và đang thực hiện thiết kế.</p>
                  </div>
                </motion.div>
              )}

              {selectedCase.status === 'approved' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 text-white"
                >
                  <div className="text-center">
                    <CheckCircle className="text-4xl mx-auto mb-3" />
                    <h3 className="text-lg font-bold mb-2">Đã Hoàn Thành</h3>
                    <p className="text-green-100">Case đã được phê duyệt và hoàn thành thành công!</p>
                  </div>
                </motion.div>
              )}

              {selectedCase.status === 'rejected' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl p-6 text-white"
                >
                  <div className="text-center">
                    <Cancel className="text-4xl mx-auto mb-3" />
                    <h3 className="text-lg font-bold mb-2">Đã Từ Chối</h3>
                    <p className="text-red-100">Case đã bị từ chối. Vui lòng xem lý do từ chối bên trên.</p>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Case #{selectedCase.id} - {selectedCase.product_id || 'N/A'}
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowDetailDialog(false)}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl font-medium transition-all duration-300 shadow-lg"
              >
                Đóng
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Review Dialog */}
      {showReviewDialog && selectedCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
          >
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Review Design
              </h3>
              <p className="text-gray-600 mb-4">
                Bạn có muốn duyệt design này không?
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Case: {selectedCase.order_id || `#${selectedCase.id}`} - {selectedCase.product_name}
              </p>
              {selectedCase.design_files && selectedCase.design_files.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Design Preview:</p>
                  <img
                    src={selectedCase.design_files[0]}
                    alt="Design preview"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowReviewDialog(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-300"
              >
                Hủy
              </button>
              <button
                onClick={handleRejectCase}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-300"
              >
                Từ chối
              </button>
              <button
                onClick={handleApproveCase}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all duration-300"
              >
                Duyệt
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      {showCancelDialog && caseToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
          >
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                  <Cancel className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">
                    Xác nhận hủy case
                  </h3>
                  <p className="text-sm text-gray-500">
                    Hành động này không thể hoàn tác
                  </p>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 border border-orange-200 mb-4">
                <p className="text-gray-700 mb-2">
                  <span className="font-semibold">Case:</span> {caseToCancel.order_id || `#${caseToCancel.id}`}
                </p>
                <p className="text-gray-600 text-sm mb-2">
                  <span className="font-semibold">Sản phẩm:</span> {caseToCancel.product_name || 'Không xác định'}
                </p>
                <p className="text-gray-600 text-sm">
                  <span className="font-semibold">Chi phí:</span> {formatCurrency(caseToCancel.cost_per_design || 0)}
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <Warning className="text-yellow-600 text-xl mt-0.5" />
                  <div>
                    <p className="text-yellow-800 font-medium mb-1">Lưu ý quan trọng:</p>
                    <ul className="text-yellow-700 text-sm space-y-1">
                      <li>• Case chưa được designer nhận</li>
                      <li>• Có thể hủy mà không ảnh hưởng đến designer</li>
                      <li>• Chi phí sẽ được hoàn lại (nếu có)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowCancelDialog(false);
                  setCaseToCancel(null);
                }}
                className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-300 font-medium"
              >
                Không hủy
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleCancelCase(caseToCancel.id)}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl transition-all duration-300 font-medium shadow-lg"
              >
                Xác nhận hủy
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DesignCaseList; 
